import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { PositionIcon } from '@triplabo/web/assets';
import { Confirm } from '@triplabo/web/components';
import {
  loadMapkit,
  isWechat,
  isIOSDevice,
  isAndroidDevice
} from '@triplabo/web/utils';
import { getCurrentPosition } from '@triplabo/web/utils/location';
import { isAscfRuntime } from '@triplabo/web/utils/ascfGeolocation';
import styles from './map.module.css';

export interface IMapProps {
  token: string;
  options?: mapkit.MapConstructorOptions;
  onLoad?: () => void;
}

export interface IMapRef {
  map: mapkit.Map | null;
}

const defaultControls = {
  showsZoomControl: true,
  showsMapTypeControl: true,
  showsUserLocationControl: false,
  tracksUserLocation: true
};

// 按运行环境选择开启定位权限的引导文案
const getPermissionGuideKey = () => {
  if (isAscfRuntime()) return 'harmony';
  if (isWechat) return 'wechat';
  if (isIOSDevice) return 'ios';
  if (isAndroidDevice) return 'android';
  return 'default';
};

const Map = forwardRef<IMapRef, IMapProps>(
  ({ token, options, onLoad }, ref) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const loaded = useRef<boolean>(false);
    const [map, setMap] = useState<mapkit.Map | null>(null);
    const [showPermissionDialog, setShowPermissionDialog] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
      if (mapRef.current) {
        loadMapkit(token).then(() => {
          if (!loaded.current) {
            const map = new mapkit.Map(mapRef.current!, {
              ...defaultControls,
              ...options
            });
            setMap(map);
            loaded.current = true;
          }
        });
      }

      return () => {
        if (map) {
          map.destroy();
          loaded.current = false;
        }
      };
    }, [map, token, options]);

    useEffect(() => {
      if (map) {
        onLoad?.();
      }
    }, [map, onLoad]);

    // 自定义定位按钮：有权限时将地图中心移动到用户当前位置，无权限时提示开启
    const handleLocate = useCallback(() => {
      if (!map) {
        return;
      }
      getCurrentPosition(
        ({ latitude, longitude }) => {
          map.setCenterAnimated(new mapkit.Coordinate(latitude, longitude));
        },
        error => {
          console.error('Error getting current position:', error);
          // 鸿蒙环境下所有定位失败都被统一为 POSITION_UNAVAILABLE，无法区分权限问题，只要失败就提示
          if (
            isAscfRuntime() ||
            (error as GeolocationPositionError)?.code ===
              GeolocationPositionError.PERMISSION_DENIED
          ) {
            setShowPermissionDialog(true);
          }
        }
      );
    }, [map]);

    useImperativeHandle(
      ref,
      () => ({
        map
      }),
      [map]
    );

    return (
      <div className={styles.wrapper}>
        <div className={styles.map} ref={mapRef}></div>
        {map && (
          <button className={styles.position} onClick={handleLocate}>
            <PositionIcon className={styles.positionIcon} />
          </button>
        )}
        <Confirm
          show={showPermissionDialog}
          content={t(
            `components.map.locationPermission.${getPermissionGuideKey()}`
          )}
          confirmText={t('components.map.locationPermissionConfirm')}
          onConfirm={() => setShowPermissionDialog(false)}
        />
      </div>
    );
  }
);

export default Map;
